import {CommonModule, isPlatformBrowser} from '@angular/common';
import {Component, EventEmitter, Inject, Input, OnDestroy, Output, PLATFORM_ID, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {DropdownModule} from 'primeng/dropdown';
import {Observable, Subscription, mergeMap, of, throwError} from 'rxjs';
import {MediaObjectRestService} from '../../api/services/media-object.rest.service';
import {FileRestService} from '../../api/services/file.rest.service';
import {MediaObjectInterface} from '../../core/interfaces/media-object.interface';
import {ImageCropperComponent} from './image-cropper.component';
import {ImageCropperModule} from './image-cropper.module';
import {ImageCropResult, ImageCropState} from './image-crop-state';

@Component({
  selector: 'app-image-crop-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ButtonModule, DialogModule, DropdownModule, ImageCropperModule],
  template: `
    <p-dialog [visible]="true" (visibleChange)="close()" (onShow)="onDialogShown()" [modal]="true" appendTo="body"
              [header]="'shared.image_cropper.edit' | translate" [draggable]="false" [resizable]="false"
              [style]="{width: '48rem', maxWidth: '96vw'}" [closeOnEscape]="!exporting" [closable]="!exporting">
      <p>{{ 'shared.image_cropper.instructions' | translate }}</p>
      <div *ngIf="!fixedRatio" class="flex align-items-center gap-2 mb-3">
        <label for="crop-ratio">{{ 'shared.image_cropper.ratio' | translate }}</label>
        <p-dropdown inputId="crop-ratio" [options]="ratios" [(ngModel)]="selectedRatio" optionValue="value"
                    [disabled]="exporting || loading" appendTo="body">
          <ng-template pTemplate="selectedItem" let-option>{{ option.label | translate }}</ng-template>
          <ng-template pTemplate="item" let-option>{{ option.label | translate }}</ng-template>
        </p-dropdown>
      </div>
      <p *ngIf="loading" role="status">{{ 'shared.image_cropper.loading' | translate }}</p>
      <p *ngIf="error" role="alert" class="text-red-500">{{ error | translate }}</p>
      <app-image-cropper *ngIf="dialogShown && file && !loading" [imageFile]="file" [editorMode]="true"
        [initialState]="cropState?.cropper"
        [externalControls]="true" [toolboxEnabled]="false" [hoverZoom]="false"
        [maintainAspectRatio]="true" [aspectRatio]="effectiveAspectRatio"
        [imageQuality]="90" format="jpeg" [resizeToWidth]="2048"
        (sourceRatio)="originalRatio = $event" (readyChange)="ready = $event"
        (onImageCropped)="accept($event)" (onImageCanceled)="loadFailed()"
        (exportFailed)="exportFailed()"></app-image-cropper>
      <ng-template pTemplate="footer">
        <button pButton type="button" class="p-button-text" [disabled]="exporting"
                [label]="'shared.image_cropper.cancel' | translate" (click)="close()"></button>
        <button *ngIf="error && !ready" pButton type="button" class="p-button-outlined"
                [label]="'shared.image_cropper.retry' | translate" (click)="load()"></button>
        <button pButton type="button" [disabled]="!ready || loading || exporting" [loading]="exporting"
                [label]="'shared.image_cropper.apply' | translate" (click)="save()"></button>
      </ng-template>
    </p-dialog>
  `,
})
export class ImageCropDialogComponent implements OnDestroy {
  @Input() sourceFile?: File;
  @Input() media?: MediaObjectInterface;
  @Input() fixedRatio?: number;
  @Input() cropState?: ImageCropState;
  @Output() accepted = new EventEmitter<ImageCropResult>();
  @Output() canceled = new EventEmitter<void>();
  @ViewChild(ImageCropperComponent) cropper?: ImageCropperComponent;
  file?: File;
  dialogShown = false;
  originalRatio = 1;
  selectedRatio = 0;
  ready = false;
  loading = false;
  exporting = false;
  error = '';
  readonly ratios = [
    {label: 'shared.image_cropper.original_ratio', value: 0},
    {label: '1:1', value: 1}, {label: '4:3', value: 4 / 3},
    {label: '3:4', value: 3 / 4}, {label: '16:9', value: 16 / 9},
  ];
  private request?: Subscription;
  private pendingState?: ImageCropState;

  constructor(private readonly files: FileRestService, @Inject(PLATFORM_ID) private readonly platform: object,
              private readonly mediaObjects: MediaObjectRestService) {}

  get effectiveAspectRatio(): number {
    return this.fixedRatio || this.selectedRatio || this.originalRatio;
  }

  onDialogShown(): void {
    if (this.dialogShown || !isPlatformBrowser(this.platform)) { return; }
    this.selectedRatio = this.cropState?.selectedRatio ?? 0;
    this.originalRatio = this.cropState?.originalRatio ?? 1;
    this.dialogShown = true;
    this.load();
  }

  load(): void {
    if (!this.dialogShown || !isPlatformBrowser(this.platform)) { return; }
    this.request?.unsubscribe();
    this.file = undefined;
    this.ready = false;
    this.error = '';
    this.loading = true;
    // Defer the cached file as well so retry recreates the cropper after a decode error.
    if (this.sourceFile) {
      this.request = new Subscription();
      const timer = setTimeout(() => { this.file = this.sourceFile; this.loading = false; });
      this.request.add(() => clearTimeout(timer));
      return;
    }
    // Collection responses may omit the original rendition. Resolve the media resource once,
    // then require its original; never edit a thumbnail or a default placeholder.
    const hasOriginal = this.media?.hyperlinks?.some(link => link.name === 'original' && link.uri);
    const media$: Observable<MediaObjectInterface | undefined> = !hasOriginal && this.media?.['@id']
      ? this.mediaObjects.getMediaObject(this.media['@id']) : of(this.media);
    this.request = media$.pipe(mergeMap(media => {
      const url = media?.hyperlinks?.find(link => link.name === 'original')?.uri;
      return url ? this.files.getFile(url) : throwError(() => new Error('Original image unavailable'));
    })).subscribe({
      next: file => { this.sourceFile = file; this.file = file; this.loading = false; },
      error: () => this.loadFailed(),
    });
  }

  loadFailed(): void {
    this.file = undefined;
    this.loading = false;
    this.ready = false;
    this.exporting = false;
    this.error = 'shared.image_cropper.load_error';
  }

  exportFailed(): void {
    this.exporting = false;
    this.error = 'shared.image_cropper.export_error';
  }

  save(): void {
    if (!this.ready || !this.cropper || this.exporting) { return; }
    const cropper = this.cropper.captureState();
    if (!cropper) { this.exportFailed(); return; }
    this.pendingState = {selectedRatio: this.selectedRatio, originalRatio: this.originalRatio, cropper};
    this.exporting = true;
    this.error = '';
    void this.cropper.cropImage();
  }

  accept(file: File): void {
    this.exporting = false;
    if (this.pendingState) {
      this.accepted.emit({file, source: this.sourceFile!, state: this.pendingState});
    }
  }

  close(): void { if (!this.exporting) { this.canceled.emit(); } }
  ngOnDestroy(): void { this.request?.unsubscribe(); }
}
